import {CommentsCategory} from '@domain/comments'
import type {CommentDto} from '@domain/comments'
import type {HistoryMaterialStorage} from '@domain/material-storage'
import type {ScheduleItem} from '@domain/schedule'
import {AssetOperationTimeSource} from '@domain/schedule'
import {AssetType, OperationModeType} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {Alert} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {ArrowForward} from '@mui/icons-material'
import {Box, Button, Grid} from '@mui/material'
import {useCommentsPermissions} from '@widgets/planning-comments'
import {useSelectedScheduleItemId} from '@widgets/planning-schedule-chart'
import type {AssetWithOperationMode} from '@widgets/planning-schedule-chart'
import type {Moment} from 'moment-timezone'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {SavingIndicator} from '..'
import {FEATURE_NAMESPACE} from '../../../consts'
import {
  PlannerFeature,
  useAvailableStoragesForOperationMode,
  useConfirmDialog,
  usePlannerFeature
} from '../../../hooks'

import {DeleteScheduleItemButton} from './DeleteScheduleItemButton'
import {ScheduleItemCommentSection} from './ScheduleItemCommentSection'
import {ScheduleItemFixedForOptimizerSwitch} from './ScheduleItemFixedForOptimizerSwitch'
import {ScheduleItemOperationModeSelect} from './ScheduleItemOperationModeSelect'
import {ScheduleItemShutdownAvailabilitySwitch} from './ScheduleItemShutdownAvailablilitySwitch'
import {ScheduleItemStandardOperationTimeInfo} from './ScheduleItemStandardOperationTimeInfo'
import {ScheduleItemStartEndSelect} from './ScheduleItemStartEndSelect'
import {ScheduleItemTransitionTimeSwitch} from './ScheduleItemTransitionTimeSwitch'
import {ScheduleItemUpdatedByInfo} from './ScheduleItemUpdatedByInfo'
import {SelectStorageSection} from './selectStorage'

const noop = () => undefined

export type AddScheduleCommentParams = {
  commentCategoryInstanceId: string
  value: string
}

export type UpdateScheduleCommentParams = {
  commentCategoryInstanceId: string
  commentId: string
  value: string
}

export type DeleteCommentParams = {
  commentCategoryInstanceId: string
  commentId: string
}

export interface EditScheduleItemFormProps {
  startOfPlan: Moment
  endOfPlan: Moment
  scheduleItem: ScheduleItem
  comment?: CommentDto
  isCommentsLoading?: boolean
  storagesById: Record<string, HistoryMaterialStorage>
  assetData: AssetWithOperationMode
  updateScheduleItem?: (item: ScheduleItem) => void
  isUpdateScheduleItemLoading?: boolean
  deleteScheduleItem?: (item: ScheduleItem) => void
  deleteComment?: (
    params: DeleteCommentParams,
    options?: {onSuccess?: () => void; onError?: () => void}
  ) => void
  isDeletingComment?: boolean
  addScheduleComment?: (
    params: AddScheduleCommentParams,
    options?: {onSuccess?: () => void}
  ) => void
  isAddCommentLoading?: boolean
  updateScheduleComment?: (params: UpdateScheduleCommentParams) => void
  isUpdateCommentLoading?: boolean
}

export const EditScheduleItemForm: React.FC<EditScheduleItemFormProps> = React.memo(
  ({
    startOfPlan,
    endOfPlan,
    comment,
    isCommentsLoading = false,
    scheduleItem,
    storagesById,
    assetData,
    updateScheduleItem,
    isUpdateScheduleItemLoading = false,
    deleteScheduleItem,
    deleteComment,
    isDeletingComment = false,
    addScheduleComment,
    isAddCommentLoading: isAddCommentLoadingProp = false,
    updateScheduleComment,
    isUpdateCommentLoading = false
  }) => {
    const {t} = useTranslation(FEATURE_NAMESPACE)
    const timezoneId = useUpmPlantTimezone()

    const {setScheduleItemId} = useSelectedScheduleItemId()
    const isShutdownAvailabilityFeatureAvailable = usePlannerFeature(
      PlannerFeature.ShutdownAvailability
    )
    const areCommentsEnabled = usePlannerFeature(PlannerFeature.PlanningComments)
    const {asset, operationMode} = assetData

    const {canViewComments} = useCommentsPermissions(CommentsCategory.AssetOperationTimes)

    const isSaving =
      isAddCommentLoadingProp || isUpdateCommentLoading || isUpdateScheduleItemLoading

    const isMaintenance = operationMode.type === OperationModeType.Maintenance

    const shouldShowCommentInput = areCommentsEnabled && canViewComments
    const shouldShowShutdownAvailability = isShutdownAvailabilityFeatureAvailable && !isMaintenance
    const shouldShowIsFixed = !isMaintenance
    const shouldShowStandardOperationTimeInfo =
      scheduleItem.source === AssetOperationTimeSource.StandardOperationTimes

    const availableStorages = useAvailableStoragesForOperationMode(operationMode, [
      startOfPlan,
      endOfPlan
    ])
    // if there is only one available storage, silo selection should be hidden,
    // except for cement mills where users can also select other silos manually
    const shouldShowSelectedSilosForm =
      !isMaintenance && (availableStorages.length > 1 || asset.type === AssetType.CementMill)

    const operationModesForCurrentAsset = asset.operationModes

    // Updates the scheduleItem with new values
    const handleOperationModeChange = (operationModeId: number) => {
      const updatedScheduleItem: ScheduleItem = {
        ...scheduleItem,
        assetOperationModeId: operationModeId
      }
      updateScheduleItem?.(updatedScheduleItem)
    }

    const handleShutdownAvailabilityChange = (isShutdownAvailable: boolean) => {
      const updatedScheduleItem: ScheduleItem = {
        ...scheduleItem,
        isShutdownAvailable
      }
      updateScheduleItem?.(updatedScheduleItem)
    }

    const handleIsFixedChange = (isFixed: boolean) => {
      const updatedScheduleItem: ScheduleItem = {
        ...scheduleItem,
        isFixed
      }
      updateScheduleItem?.(updatedScheduleItem)
    }

    const handleTransitionTimeChange = (isTransitionTime: boolean) => {
      const updatedScheduleItem: ScheduleItem = {
        ...scheduleItem,
        isTransitionTime: isTransitionTime
      }
      updateScheduleItem?.(updatedScheduleItem)
    }

    const {openDialog} = useConfirmDialog()

    const updateWithSplitWarning =
      <T extends number | boolean>(updateFn: (value: T) => void) =>
      (value: T, shouldSplit: boolean) => {
        if (!shouldSplit) {
          return updateFn(value)
        }
        openDialog({
          title: operationMode.name,
          description: t('planning.scheduleItemSplitInfo'),
          testId: 'split_warning_dialog',
          additionalContent: (
            <Alert accentColor="warning" content={t('planning.scheduleItemSplitWarning')} />
          ),
          mainAction: {
            text: t('common.proceed'),
            icon: <ArrowForward />,
            onAction: () => updateFn(value)
          }
        })
      }

    const onOperationModeChange = updateWithSplitWarning(handleOperationModeChange)
    const onTransitionTimeChange = updateWithSplitWarning(handleTransitionTimeChange)

    return (
      <>
        <Grid container spacing={1.5} {...dataTestId('edit_schedule_item_form')}>
          {operationModesForCurrentAsset.length > 1 && (
            <Grid xs={12} item>
              <ScheduleItemOperationModeSelect
                scheduleItem={scheduleItem}
                operationModes={operationModesForCurrentAsset}
                handleOperationModeChange={onOperationModeChange}
              />
            </Grid>
          )}
          <Grid xs={12} item>
            <ScheduleItemStartEndSelect
              scheduleItem={scheduleItem}
              startOfPlan={startOfPlan}
              endOfPlan={endOfPlan}
              updateScheduleItem={updateScheduleItem ?? noop}
            />
          </Grid>
          <Grid xs={12} item>
            <ScheduleItemUpdatedByInfo scheduleItem={scheduleItem} />
          </Grid>
          {shouldShowStandardOperationTimeInfo && (
            <Grid xs={12} item>
              <ScheduleItemStandardOperationTimeInfo scheduleItem={scheduleItem} />
            </Grid>
          )}
          <Grid xs={12} item>
            <ScheduleItemTransitionTimeSwitch
              scheduleItem={scheduleItem}
              handleTransitionTimeChange={onTransitionTimeChange}
            />
          </Grid>
          {shouldShowShutdownAvailability && (
            <Grid xs={12} item>
              <ScheduleItemShutdownAvailabilitySwitch
                scheduleItem={scheduleItem}
                handleShutdownAvailabilityChange={handleShutdownAvailabilityChange}
              />
            </Grid>
          )}
          {shouldShowIsFixed && (
            <Grid xs={12} item>
              <ScheduleItemFixedForOptimizerSwitch
                scheduleItem={scheduleItem}
                handleShutdownAvailabilityChange={handleIsFixedChange}
              />
            </Grid>
          )}
          {shouldShowSelectedSilosForm && (
            <Grid xs={12} item>
              <SelectStorageSection
                asset={asset}
                scheduleItem={scheduleItem}
                startOfPlan={startOfPlan}
                endOfPlan={endOfPlan}
                storagesById={storagesById}
                availableStorages={availableStorages}
                updateScheduleItem={updateScheduleItem}
              />
            </Grid>
          )}
          {shouldShowCommentInput ? (
            <Grid xs={12} item>
              <ScheduleItemCommentSection
                comment={comment}
                isCommentsLoading={isCommentsLoading}
                timezoneId={timezoneId}
                scheduleItem={scheduleItem}
                addScheduleComment={addScheduleComment}
                isAddCommentLoading={isAddCommentLoadingProp}
                updateScheduleComment={updateScheduleComment}
                deleteComment={deleteComment}
                isDeletingComment={isDeletingComment}
              />
            </Grid>
          ) : null}
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: 1,
            my: 2
          }}
        >
          <DeleteScheduleItemButton
            scheduleItem={scheduleItem}
            comment={areCommentsEnabled ? comment : undefined}
            isReadOnly={isSaving}
            deleteScheduleItem={deleteScheduleItem}
            deleteComment={deleteComment}
          />
          <Box display="flex" alignItems="center">
            <SavingIndicator mr={2} {...dataTestId('saving_indicator')} isSaving={isSaving} />
            <Button
              {...dataTestId('schedule_edit_save')}
              color="primary"
              variant="contained"
              startIcon={<ArrowForward />}
              disabled={isSaving}
              onClick={() => setScheduleItemId(undefined)}
            >
              {t('common.done')}
            </Button>
          </Box>
        </Box>
      </>
    )
  }
)
